import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { SectionView } from "@/components/pageSections";
import useSeo from "@/hooks/useSeo";

export default function PublicLanding() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const fnlSlug = search.get("fnl");
  const fnlStep = search.get("step");
  const [page, setPage] = useState(null);
  const [funnelStep, setFunnelStep] = useState(null);
  const [err, setErr] = useState(false);
  const [activeVariant, setActiveVariant] = useState("a");
  const trackedRef = useRef(false);

  // SEO — uses creator's per-page seo_title/seo_description/cover_image if set,
  // else smart fallback. cover_image drives the OG image for WhatsApp/Twitter/LinkedIn.
  useSeo({
    title: page?.seo_title || (page?.title ? `${page.title} — Videoepreneur` : undefined),
    description: page?.seo_description || (page ? `Join ${page.title}. Reserve your spot on this live workshop hosted on Videoepreneur.` : undefined),
    image: page?.cover_image || page?.brand_logo,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/pages/${slug}`);
        setPage(data);
        if (data.seo_title) document.title = data.seo_title;

        if (data.ab_enabled && data.variant_b) {
          const stickyKey = `vp_ab_${slug}`;
          let choice = localStorage.getItem(stickyKey);
          if (!choice) {
            choice = Math.random() < 0.5 ? "a" : "b";
            localStorage.setItem(stickyKey, choice);
          }
          setActiveVariant(choice);
        }
      } catch { setErr(true); }
    })();
  }, [slug]);

  // Load funnel step (if part of a funnel)
  useEffect(() => {
    if (!fnlSlug || fnlStep == null) { setFunnelStep(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/funnels/${fnlSlug}/step/${fnlStep}`);
        setFunnelStep(data);
      } catch { setFunnelStep(null); }
    })();
  }, [fnlSlug, fnlStep]);

  // A/B view beacon
  useEffect(() => {
    if (!page || trackedRef.current) return;
    if (!page.ab_enabled) return;
    trackedRef.current = true;
    axios.post(`${API_BASE}/public/pages/${slug}/track?variant=${activeVariant}&event=view`).catch(() => {});
  }, [page, activeVariant, slug]);

  // Funnel view beacon
  const funnelTrackedRef = useRef(false);
  useEffect(() => {
    if (!funnelStep || funnelTrackedRef.current) return;
    funnelTrackedRef.current = true;
    axios.post(`${API_BASE}/public/funnels/${fnlSlug}/track?step_id=${funnelStep.step_id}&event=view`).catch(() => {});
  }, [funnelStep, fnlSlug]);

  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <div className="vp-label mb-3">404</div>
        <h1 className="font-display text-3xl font-bold">Page not found or unpublished.</h1>
      </div>
    );
  }
  if (!page) return <div className="min-h-screen flex items-center justify-center text-[#4A4D57]">Loading…</div>;

  const sections = (activeVariant === "b" && page.variant_b?.sections) ? page.variant_b.sections : page.sections;
  if (typeof window !== "undefined") window.__vp_active_page_slug = slug;

  // Determine funnel next-step URL for CTAs
  const funnelNext = funnelStep && funnelStep.next_slug
    ? {
        href: `/p/${funnelStep.next_slug}?fnl=${fnlSlug}&step=${funnelStep.next_order}`,
        label: funnelStep.next_label,
        onClick: () => {
          axios.post(`${API_BASE}/public/funnels/${fnlSlug}/track?step_id=${funnelStep.step_id}&event=conversion`).catch(() => {});
          // also A/B conversion if enabled
          if (page.ab_enabled) {
            axios.post(`${API_BASE}/public/pages/${slug}/track?variant=${activeVariant}&event=conversion`).catch(() => {});
          }
        },
      }
    : null;

  // Font family from Google Fonts, loaded on-demand. Matches editor logic.
  const fam = page.font_family || "Inter";
  const famUrl = fam.replace(/ /g, "+");
  const scaleMap = { s: 0.9, m: 1, l: 1.1 };
  const rootFontSize = (scaleMap[page.font_scale || "m"] || 1) * 16;

  return (
    <div
      className="min-h-screen"
      style={{ fontFamily: `"${fam}", system-ui, sans-serif`, fontSize: `${rootFontSize}px` }}
    >
      <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?family=${famUrl}:wght@400;600;700;800;900&display=swap`} />
      {/* Sticky public header — logo + Reserve CTA */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-[#E4E6EC]">
        <div className="max-w-[1200px] mx-auto px-3 sm:px-8 py-3 sm:py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {page.brand_logo ? (
              <img src={page.brand_logo} alt="logo" className="h-7 sm:h-8 w-auto object-contain flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center font-display font-black text-white text-base sm:text-lg flex-shrink-0" style={{ background: page.accent_color || "#0B5CFF" }}>
                {(page.title || "V")[0]?.toUpperCase()}
              </div>
            )}
            <div className="font-display font-bold text-xs sm:text-base text-[#0B0D17] truncate">{page.title}</div>
          </div>
          <a href="#cta" className="vp-btn-primary text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-5 whitespace-nowrap flex-shrink-0" style={{ background: page.accent_color, borderColor: page.accent_color }}>
            Enroll Now
          </a>
        </div>
      </header>

      {funnelStep && (
        <div className="w-full bg-[#0B5CFF]/8 border-b border-[#0B5CFF]/20 py-2 text-center text-xs text-[#0B0D17]">
          Step {parseInt(fnlStep, 10) + 1} of {funnelStep.total_steps} · <span className="font-semibold">{funnelStep.label}</span>
        </div>
      )}
      {sections.map((s, i) => (
        <SectionView key={s.id || i} section={s} accent={page.accent_color} funnelNext={funnelNext} />
      ))}

      {/* Footer */}
      <footer className="border-t border-[#E4E6EC] bg-[#F6F7FA] py-8">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 text-center text-xs text-[#747680]">
          © {new Date().getFullYear()} {page.title} · Powered by Videoepreneur
        </div>
      </footer>
    </div>
  );
}
